import moment from 'moment'
/**
 * Chart formatting helper functions
 * Provides consistent number and currency formatting across all charts
 */

/**
 * Get display label for aggregation method
 * @param {string} aggregation - The aggregation method (e.g., 'sum', 'count', 'distinct_count')
 * @returns {string} Formatted display label
 */
export const getAggregationLabel = (aggregation) => {
  const labels = {
    sum: 'Sum',
    count: 'Count',
    avg: 'Average',
    min: 'Minimum',
    max: 'Maximum',
    distinct_count: 'Distinct Count'
  }
  return labels[aggregation] || aggregation
}

/**
 * Format a number with appropriate decimal places for display
 * @param {number} value - The number to format
 * @param {number} maxDecimals - Maximum decimal places (default: 1)
 * @param {number} minDecimals - Minimum decimal places (default: 0, i.e. trailing zeros dropped)
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, maxDecimals = 1, minDecimals = 0) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0'
  }
  
  return Number(value).toLocaleString('en-US', {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals
  })
}

/**
 * Format a value as currency (USD)
 * @param {number} value - The number to format as currency
 * @param {boolean} includeDecimals - Whether to include decimal places (default: false)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, includeDecimals = false) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '$0'
  }
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: includeDecimals ? 2 : 0,
    maximumFractionDigits: includeDecimals ? 2 : 0
  }).format(value)
}

/**
 * Format large numbers with K, M, B, T suffixes when exceeding 6 characters
 * @param {number} value - The number to format
 * @param {boolean} isCurrency - Whether this is a currency value
 * @returns {string} Formatted compact number string
 */
export const formatCompactNumber = (value, isCurrency = false) => {
  if (value === null || value === undefined || isNaN(value)) {
    return isCurrency ? '$0' : '0'
  }

  const absValue = Math.abs(value)
  const isNegative = value < 0
  const prefix = isCurrency ? '$' : ''
  const sign = isNegative ? '-' : ''

  let formatted
  if (absValue >= 1e12) {
    formatted = (absValue / 1e12).toFixed(2) + 'T'
  } else if (absValue >= 1e9) {
    formatted = (absValue / 1e9).toFixed(2) + 'B'
  } else if (absValue >= 1e6) {
    formatted = (absValue / 1e6).toFixed(2) + 'M'
  } else if (absValue >= 1e3) {
    formatted = (absValue / 1e3).toFixed(2) + 'K'
  } else {
    return isCurrency
      ? formatCurrency(value, false)
      : formatNumber(value, 1)
  }

  // Remove trailing zeros after decimal point and unnecessary decimal point
  formatted = formatted.replace(/\.?0+([KMBT])$/, '$1')

  return `${sign}${prefix}${formatted}`
}

/**
 * The per-column choice of how a date reads, mirroring Charts::DateFormat on the API so
 * the browser, the CSV/PDF exports and the snapshot renderer all agree.
 *
 * A pivot row grouping also carries a date grouping (day/week/month/year), so each format
 * declares how it reads at each: 'numeric' at a month grouping is "07/2026", not
 * "07/01/2026". A year grouping is always the bare year — no format writes it differently.
 */
export const DATE_FORMAT_PATTERNS = {
  long: { day: 'MMMM D, YYYY', month: 'MMMM YYYY' },
  medium: { day: 'MMM D, YYYY', month: 'MMM YYYY' },
  numeric: { day: 'MM/DD/YYYY', month: 'MM/YYYY' },
  short: { day: 'M/D/YY', month: 'M/YY' },
  iso: { day: 'YYYY-MM-DD', month: 'YYYY-MM' }
}

// The format every table used before the choice existed, so an unset column keeps
// rendering exactly as it always has.
export const DATE_FORMAT_DEFAULT = 'long'

/**
 * The moment.js pattern for a date format at a given grouping. A week grouping labels the
 * week's start date, so it reuses the day pattern; the "Week of" prefix is added by the
 * caller rendering the label.
 * @param {string} dateFormat - One of the DATE_FORMAT_PATTERNS keys
 * @param {string} dateGrouping - The date grouping type ('day', 'week', 'month', 'year')
 * @returns {string} A moment format string
 */
export const dateFormatPattern = (dateFormat, dateGrouping = 'day') => {
  const patterns = DATE_FORMAT_PATTERNS[dateFormat] || DATE_FORMAT_PATTERNS[DATE_FORMAT_DEFAULT]

  if (dateGrouping === 'year') return 'YYYY'
  if (dateGrouping === 'month') return patterns.month
  return patterns.day
}

/**
 * Format a date value based on grouping
 * @param {string} dateValue - The date value to format (YYYY-MM-DD or ISO datetime)
 * @param {string} dateGrouping - The date grouping type ('day', 'week', 'month', 'year')
 * @param {string} dateFormat - The column's date format ('long', 'medium', 'numeric', 'short', 'iso')
 * @returns {string} Formatted date string
 */
export const formatDateByGrouping = (dateValue, dateGrouping = 'day', dateFormat = DATE_FORMAT_DEFAULT) => {
  if (!dateValue) {
    return 'None'
  }

  // Parsed and formatted in UTC, same rationale as formatDateTimeValue: date
  // values are serialized as UTC midnight, so local-zone rendering shifts the
  // calendar day backwards in US timezones.
  const date = moment.utc(dateValue)
  const formatted = date.format(dateFormatPattern(dateFormat, dateGrouping))

  return dateGrouping === 'week' ? `Week of ${formatted}` : formatted
}

// Matches the leading date portion of an ISO-8601 string, e.g. "2026-07-30"
// or "2026-07-30T00:00:00.000Z". Used only as a cheap pre-filter — the strict
// parse inside formatDateTimeValue is the authoritative check.
const ISO_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2})?/

/**
 * True when the value is a string that looks like an ISO-8601 date/datetime.
 * @param {*} value - The value to test
 * @returns {boolean}
 */
export const looksLikeIsoDate = (value) =>
  typeof value === 'string' && ISO_DATE_PREFIX.test(value)

/**
 * Format a raw date/datetime value (typically an ISO string such as
 * "2026-07-30T00:00:00.000Z") into a human-readable label for table cells.
 *
 * Formatted in UTC on purpose: date-semantic values are serialized as UTC
 * midnight, and rendering them in the browser's local zone shifts the calendar
 * day backwards in US timezones (same rationale as
 * helpers/datetime#formatDateSlash). The time is appended only when the value
 * carries a non-midnight-UTC time component. Non-date values are returned
 * unchanged, so this is safe to call on any cell value.
 *
 * @param {string|Date} value - The value to format
 * @param {string} dateFormat - The column's date format ('long', 'medium', 'numeric', 'short', 'iso')
 * @returns {string} Human-readable date/datetime, or the original value stringified
 */
export const formatDateTimeValue = (value, dateFormat = DATE_FORMAT_DEFAULT) => {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  let parsed
  if (value instanceof Date) {
    parsed = moment.utc(value)
  } else if (looksLikeIsoDate(value)) {
    // looksLikeIsoDate guarantees a full YYYY-MM-DD prefix, so the strict ISO
    // parser can't mistake a bare number like "1200" for a year.
    parsed = moment.utc(value, moment.ISO_8601, true)
  } else {
    // Not a date (text, a bare number, "7/30/2026", ...) — leave it untouched.
    return String(value)
  }

  if (!parsed.isValid()) {
    return String(value)
  }

  const datePattern = dateFormatPattern(dateFormat)
  const hasTime = parsed.hour() !== 0 || parsed.minute() !== 0 || parsed.second() !== 0
  return parsed.format(hasTime ? `${datePattern}, h:mm A` : datePattern)
}

/**
 * The decimal_places a column explicitly asks for, or null when it has no preference.
 *
 * Pivot column aggregations and data table columns each carry a per-column choice of 0
 * (round to a whole number) or 2 (show cents); every other caller passes a column meta
 * without the key and keeps the historical defaults below.
 *
 * @param {object} column - Column configuration object
 * @returns {number|null} 0, 2, or null when unset
 */
const explicitDecimalPlaces = (column) => {
  const requested = column?.decimal_places
  return requested === 0 || requested === 2 ? requested : null
}

/**
 * Format a value based on column display type
 * @param {number} value - The value to format
 * @param {object} column - Column configuration object
 * @param {string} aggregation - The aggregation method used (e.g., 'count', 'sum', 'avg') OR date grouping for date columns
 * @param {boolean} useCompact - Whether to use compact format for large numbers (default: true for KPI)
 * @returns {string} Formatted value string
 */
export const formatValueByColumn = (value, column, aggregation = null, useCompact = false) => {
  if (value === null || value === undefined) {
    return '0'
  }

  // Handle date columns with date grouping
  if (column?.display_as === 'date' && aggregation) {
    // When aggregation parameter is used for date grouping. The column's own date_format
    // decides how that grouped date reads ("07/2026" vs "July 2026").
    return formatDateByGrouping(value, aggregation, column?.date_format)
  }

  const decimals = explicitDecimalPlaces(column)

  // Don't format currency for count or distinct_count aggregations. Counts are always
  // whole records, so a column's decimal_places choice never applies here — the editor
  // hides the control for these methods rather than letting them render "12.00".
  if (aggregation === 'count' || aggregation === 'distinct_count') {
    const formatted = formatNumber(value, 0)
    // Check if the formatted value exceeds 6 characters and use compact format
    if (useCompact && formatted.replace(/,/g, '').length > 6) {
      return formatCompactNumber(value, false)
    }
    return formatted
  }

  // Format based on column display type
  if (column?.display_as === 'money' || column?.display_as === 'two_digit_money') {
    const formatted = formatCurrency(value, decimals === null ? false : decimals > 0)
    // Check if the formatted value exceeds 6 characters (excluding $) and use compact format
    if (useCompact && formatted.replace(/[$,]/g, '').length > 6) {
      return formatCompactNumber(value, true)
    }
    return formatted
  }

  // Default number formatting with up to 1 decimal place. A column that asked for a
  // specific number of decimals gets exactly that many, trailing zeros included, so
  // "two decimal places" reads as 12.00 rather than 12.
  const formatted = decimals === null
    ? formatNumber(value, 1)
    : formatNumber(value, decimals, decimals)
  if (useCompact && formatted.replace(/,/g, '').length > 6) {
    return formatCompactNumber(value, false)
  }
  return formatted
}

/**
 * Create a Highcharts tooltip formatter function
 * @param {object} options - Formatter options
 * @param {object} options.column - Column configuration
 * @param {string} options.aggregation - Aggregation method
 * @param {boolean} options.isGrouped - Whether the chart has multiple series
 * @param {string} options.prefix - Optional prefix for the value
 * @param {string} options.suffix - Optional suffix for the value
 * @returns {function} Highcharts tooltip formatter function
 */
export const createTooltipFormatter = (options = {}) => {
  const { column, aggregation, isGrouped, prefix = '', suffix = '' } = options

  return function() {
    // Handle grouped/multi-series tooltips
    if (isGrouped && this.points) {
      let tooltip = `<b>${this.x}</b><br/>`
      this.points.forEach((point) => {
        const formattedValue = formatValueByColumn(point.y, column, aggregation)
        const displayValue = `${prefix}${formattedValue}${suffix}`
        tooltip += `<span style="color:${point.color}">●</span> ${point.series.name}: ${displayValue}<br/>`
      })
      return tooltip
    }

    // Handle single series tooltips
    const formattedValue = formatValueByColumn(this.y, column, aggregation)
    const displayValue = `${prefix}${formattedValue}${suffix}`
    
    // For bar charts and similar, use this.key if available
    const label = this.x || this.key || this.point?.name || ''
    return `<b>${label}</b>: ${displayValue}`
  }
}

/**
 * Create a data label formatter for charts
 * @param {object} options - Formatter options
 * @param {object} options.column - Column configuration
 * @param {string} options.aggregation - Aggregation method
 * @returns {function} Highcharts data label formatter function
 */
export const createDataLabelFormatter = (options = {}) => {
  const { column, aggregation } = options
  
  return function() {
    return formatValueByColumn(this.y, column, aggregation)
  }
}

/**
 * Format percentage values for stacked percentage charts
 * @param {number} value - The percentage value
 * @param {number} total - The total value (optional)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, total = null) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%'
  }
  
  let percentage = value
  if (total && total !== 0) {
    percentage = (value / total) * 100
  }
  
  return `${formatNumber(percentage, 1)}%`
}